/**
 * Analyzer Service
 * Manages Azure AI Content Understanding analyzers and configurations.
 * All requests include Bearer token + x-tenant-id via authFetch.
 */

import { StageConfiguration } from '../types';
import { authFetch } from './authFetch';
import configurationService from './configurationService';

export interface AnalyzerInfo {
  analyzerId: string;
  description: string;
  baseAnalyzerId: string;
  createdDateTime?: string;
  fieldSchema?: {
    fields: {
      [fieldName: string]: {
        type: 'string' | 'number' | 'date' | 'boolean' | 'array' | 'object';
        method?: 'extract' | 'generate';
        description?: string;
      };
    };
  };
}

/** Per-analyzer export config (Sprint M3) — drives API submit vs scheduled batch export. */
export interface AnalyzerExportConfig {
  mode: 'api' | 'batch';
  batchFormat?: 'csv' | 'xml' | 'fixed-width' | 'pipe-delimited';
  schedule?: {
    /** 24h local time "HH:mm". */
    time: string;
    /** IANA timezone, e.g. "America/New_York". */
    timezone: string;
  };
}

export interface AnalyzerConfiguration {
  analyzerId: string;
  analyzerName: string;
  documentType: string;
  enabled: boolean;
  enableOwnershipInference: boolean;
  stages?: StageConfiguration[];
  fields: FieldConfiguration[];
  /** Export configuration (Sprint M3). */
  export?: AnalyzerExportConfig;
  /**
   * When true, every document processed by this analyzer is routed to PENDING_VALIDATION
   * regardless of extraction confidence. No document is ever auto-approved.
   */
  forceHitl?: boolean;
}

export type FieldDataType = 'string' | 'date' | 'number' | 'currency' | 'address' | 'boolean';

/**
 * Provenance values that can be CONFIGURED on a field definition (Lens S1). Data-only mirror
 * of `azure-functions/src/types/analyzer.ts`'s `FIELD_PROVENANCE_CONFIG` — kept in step by
 * `analyzerContract.drift.test.ts`, which fails if the two lists diverge.
 *
 * Static per-field truths, identical for every document of the type. Absent means `'extracted'`.
 * `'derived'` — computed from other extracted fields. `'enriched'` — appended by an enricher.
 *
 * **Deliberately NOT named `source`** — that name is already taken twice elsewhere in the
 * codebase, on different objects, where a mix-up would type-check and fail silently.
 */
export const FIELD_PROVENANCE_CONFIG = ['extracted', 'derived', 'enriched'] as const;
export type FieldProvenance = (typeof FIELD_PROVENANCE_CONFIG)[number];

/**
 * Provenance values that can appear on a STORED VALUE — the configurable set plus the two that
 * vary document to document and so cannot live in analyzer config: `'operator'` (typed by a
 * clerk during HITL) and `'inbound'` (returned by the SOR after delivery).
 */
export const FIELD_PROVENANCE_VALUE = [...FIELD_PROVENANCE_CONFIG, 'operator', 'inbound'] as const;
export type FieldValueProvenance = (typeof FIELD_PROVENANCE_VALUE)[number];

/** Per-field validation rule (Sprint M2). `pattern` is a JS-compatible regex string. */
export interface FieldValidationRule {
  pattern?: string;
  min?: number;
  max?: number;
  message?: string;
}

/**
 * Declarative deterministic normalization (Sprint N1/N4). Data-only mirror of
 * `azure-functions/src/types/analyzer.ts`'s `FieldNormalization` — applied by
 * `fieldNormalizationService` post-extraction, zero tokens.
 */
export interface FieldNormalization {
  removePunctuation?: boolean;
  removeDots?: boolean;
  stripWords?: string[];
  regexReplace?: Array<{ pattern: string; replacement: string; flags?: string }>;
  dateReformat?: { from?: string; to: string };
  valueMap?: Record<string, string>;
  padLeft?: { length: number; char?: string };
  truncate?: { length: number };
  /**
   * SLM / AI transform mode (Processing Pipeline UX redesign) — a prompt-tuned
   * small-language-model transformation that incurs AI token cost. Optional and
   * additive: when absent the field uses deterministic normalization only, so
   * existing stored configurations deserialize unchanged.
   */
  slm?: { prompt: string; model: string };
}

export interface FieldConfiguration {
  fieldName: string;
  displayName: string;
  extractionDescription?: string;
  fieldType: 'string' | 'date' | 'number' | 'address' | 'currency';
  enabled: boolean;
  required: boolean;
  readOnly: boolean;
  uiGroup: 'verification' | 'newInformation' | 'hidden';
  displayOrder: number;
  /**
   * Pipeline steps that reference this field (display/lineage only — e.g. 'extract',
   * 'normalize', 'enrich', 'validate', 'deliver'). No longer the old fixed HITL-stage triple:
   * the HITL flow is derived from pipeline outputs, and field visibility is driven by `uiGroup`.
   */
  stageUsage: string[];
  externalMapping?: { grmField?: string };
  validation?: { defaultValue?: string };
  // Field schema (Sprint M2) — drives dynamic HITL rendering + confidence routing
  dataType?: FieldDataType;
  /** Display/format hint shown in HITL, e.g. "MM/DD/YYYY" or "$#,##0.00". */
  format?: string;
  /** Per-field confidence threshold (0–1). Below this flags the field for review. */
  confidenceThreshold?: number;
  validationRule?: FieldValidationRule;
  /** Opt a dataType:'address' field into structured address validation (Sprint F2). */
  addressValidate?: { enabled: boolean; country: string };
  // Export field-mapping (Sprint M3) — drives API submit + batch export.
  /** Target field name in the exported record. Defaults to grmField → displayName → fieldName. */
  exportName?: string;
  /** Whether the field is included in the export. Defaults to true. */
  exportInclude?: boolean;
  /** Optional value transform on export. */
  exportTransform?: 'uppercase' | 'lowercase' | 'trim' | 'titlecase';
  // Deterministic post-extraction normalization (Sprint N1/N4) — zero tokens.
  textFormatting?: {
    case?: 'uppercase' | 'lowercase' | 'titlecase';
    trimTrailing?: string[];
  };
  normalize?: FieldNormalization;
  /**
   * Which Normalize mode is active for this field (Processing Pipeline UX redesign).
   * Semantically defaults to 'deterministic' when absent — kept optional so existing
   * stored configurations deserialize unchanged with no serialization behavior change.
   */
  normalizeMode?: 'deterministic' | 'slm';
  // Retrieval contract (Lens S1) — additive and inert, mirroring `FieldMetadata` in
  // `azure-functions/src/types/analyzer.ts`. No runtime consumer yet: these exist so the
  // query API (S2) and the inbound-data block (R1) have a shared vocabulary. Absent
  // means current behavior in every case.
  /** Where this field's values come from, for every document of this type. Absent means `'extracted'`. */
  provenance?: FieldProvenance;
  /** Whether the field is exposed as a search predicate and MAY be projected. Absent means not searchable. */
  searchable?: boolean;
  /**
   * Whether the field renders as a default Lens result column (R9). Decoupled from `searchable` so a
   * field can be findable without cluttering the table. **Absent falls back to `searchable`** — every
   * config saved before R9 keeps its columns.
   */
  showInResults?: boolean;
  /**
   * Whether the result-column value clips to the column width with an ellipsis (R9). Absent means
   * `true`: adding a long-text field as a column must not silently produce unscrollable rows.
   *
   * Named `truncateColumn`, not `truncate`, to stay distinct from `normalize.truncate` on this same
   * field — that one rewrites the stored value; this one only clips at render.
   */
  truncateColumn?: boolean;
  /**
   * Relative ranking weight when `searchable`. Absent means default weighting.
   * Range and default are deliberately unset here — S2 fixes them to the retrieval
   * engine it selects, and S8 validates against that same range.
   */
  searchWeight?: number;
}

/**
 * Preserves prior per-field settings (uiGroup, required, confidenceThreshold, validationRule,
 * normalize/normalizeMode, export*, etc.) across an analyzer schema edit. Only the extraction
 * description and field type — the two things a Content Understanding schema edit actually
 * changes — are taken from `editedFields`; everything else survives from `priorFields` for any
 * field name that already existed. A field name with no prior match (newly added) keeps its
 * edited/default values untouched.
 */
export function mergeFieldConfigurations(
  priorFields: FieldConfiguration[],
  editedFields: FieldConfiguration[],
  renameMap?: Map<string, string>
): FieldConfiguration[] {
  const priorByName = new Map(priorFields.map((f) => [f.fieldName, f]));
  // Build reverse lookup (newName → oldName) once for O(1) per-field resolution
  const reverseRenameMap = renameMap
    ? new Map([...renameMap.entries()].map(([oldName, newName]) => [newName, oldName]))
    : undefined;
  return editedFields.map((edited) => {
    // Check if this new field name is the target of a rename
    const oldName = reverseRenameMap?.get(edited.fieldName);
    const prior = priorByName.get(edited.fieldName) ?? (oldName ? priorByName.get(oldName) : undefined);
    if (!prior) return edited;
    return {
      ...prior,
      fieldName: edited.fieldName,
      extractionDescription: edited.extractionDescription,
      fieldType: edited.fieldType,
    };
  });
}

// ── Field Change Detection (Pipeline Propagation) ──────────────────────────────

/** A single detected change between prior and edited field sets. */
export interface FieldChange {
  type: 'rename' | 'remove' | 'add' | 'descriptionChange';
  oldName?: string;
  newName?: string;
  fieldName?: string;
}

/**
 * Detects field-level changes between a prior and edited field array.
 *
 * Rename heuristic: a field that disappeared from the prior set and a new field that appeared
 * in the edited set at the same array position with the same fieldType are treated as a rename.
 * This is best-effort — the confirmation dialog lets the admin correct false positives.
 */
export function detectFieldChanges(
  priorFields: FieldConfiguration[],
  editedFields: FieldConfiguration[]
): FieldChange[] {
  const changes: FieldChange[] = [];
  const priorNames = new Set(priorFields.map((f) => f.fieldName));
  const editedNames = new Set(editedFields.map((f) => f.fieldName));

  const removed = priorFields.filter((f) => !editedNames.has(f.fieldName));
  const added = editedFields.filter((f) => !priorNames.has(f.fieldName));

  // Attempt rename matching: same position + same type
  const matchedRenames = new Set<string>();
  const matchedAdds = new Set<string>();

  for (const removedField of removed) {
    const priorIdx = priorFields.findIndex((f) => f.fieldName === removedField.fieldName);
    // Look for an added field at the same position with the same type
    const candidate = added.find(
      (a) =>
        !matchedAdds.has(a.fieldName) &&
        editedFields.findIndex((f) => f.fieldName === a.fieldName) === priorIdx &&
        a.fieldType === removedField.fieldType
    );
    if (candidate) {
      changes.push({ type: 'rename', oldName: removedField.fieldName, newName: candidate.fieldName });
      matchedRenames.add(removedField.fieldName);
      matchedAdds.add(candidate.fieldName);
    }
  }

  // Remaining removals (not matched as renames)
  for (const removedField of removed) {
    if (!matchedRenames.has(removedField.fieldName)) {
      changes.push({ type: 'remove', fieldName: removedField.fieldName });
    }
  }

  // Remaining additions (not matched as renames)
  for (const addedField of added) {
    if (!matchedAdds.has(addedField.fieldName)) {
      changes.push({ type: 'add', fieldName: addedField.fieldName });
    }
  }

  // Description changes for fields that still exist
  for (const edited of editedFields) {
    if (!priorNames.has(edited.fieldName)) continue;
    const prior = priorFields.find((f) => f.fieldName === edited.fieldName);
    if (prior && prior.extractionDescription !== edited.extractionDescription) {
      changes.push({ type: 'descriptionChange', fieldName: edited.fieldName });
    }
  }

  return changes;
}

/**
 * Builds a rename map from detected field changes (type === 'rename').
 * Keys are old names, values are new names.
 */
export function buildRenameMap(changes: FieldChange[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const change of changes) {
    if (change.type === 'rename' && change.oldName && change.newName) {
      map.set(change.oldName, change.newName);
    }
  }
  return map;
}

export class AnalyzerService {
  async listAnalyzers(): Promise<AnalyzerInfo[]> {
    const response = await authFetch('/analyzers');
    if (!response.ok) throw new Error(`Failed to list analyzers: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to list analyzers');
    return data.analyzers || [];
  }

  async getAnalyzer(analyzerId: string): Promise<AnalyzerInfo> {
    const response = await authFetch(`/analyzers/details?analyzerId=${encodeURIComponent(analyzerId)}`);
    if (!response.ok) throw new Error(`Failed to get analyzer: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to get analyzer');
    return data.analyzer;
  }

  async createAnalyzer(
    analyzerId: string,
    description: string,
    baseAnalyzerId: string,
    fieldSchema: any
  ): Promise<{ operationId: string }> {
    const response = await authFetch('/analyzers', {
      method: 'POST',
      body: JSON.stringify({ analyzerId, description, baseAnalyzerId, fieldSchema })
    });
    if (!response.ok) throw new Error(`Failed to create analyzer: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to create analyzer');
    return { operationId: data.operationId };
  }

  async saveAnalyzerMetadata(config: AnalyzerConfiguration): Promise<void> {
    const metadata = {
      analyzerId: config.analyzerId,
      documentType: config.documentType,
      fields: {} as any,
      stages: config.stages || [],
      // Export config (Sprint M3) — API vs batch, format, schedule.
      ...(config.export ? { export: config.export } : {})
    };

    config.fields.forEach(field => {
      metadata.fields[field.fieldName] = {
        displayName: field.displayName,
        displayOrder: field.displayOrder,
        uiGroup: field.uiGroup,
        required: field.required,
        readOnly: field.readOnly,
        stageUsage: field.stageUsage,
        grmMapping: field.externalMapping?.grmField,
        validation: field.validation,
        // Field schema (Sprint M2)
        dataType: field.dataType,
        format: field.format,
        confidenceThreshold: field.confidenceThreshold,
        validationRule: field.validationRule,
        addressValidate: field.addressValidate,
        // Export field-mapping (Sprint M3)
        exportName: field.exportName,
        exportInclude: field.exportInclude,
        exportTransform: field.exportTransform,
        // Deterministic post-extraction normalization (Sprint N1/N4)
        textFormatting: field.textFormatting,
        normalize: field.normalize,
        // SLM normalization mode (Processing Pipeline Solidification — Req 4.1)
        normalizeMode: field.normalizeMode,
        // Lens field contract (S1). Carried through the save round trip as of S4: the search
        // screen builds its result columns from `searchable`, so a save of any UNRELATED change
        // in this UI would otherwise silently clear every configured column.
        provenance: field.provenance,
        searchable: field.searchable,
        // Lens result-column contract (R9). Carried through the save round trip for the same reason
        // `searchable` is: the search screen derives its columns from these, so an unrelated save
        // must not silently clear a tenant's column layout, order or truncation choices.
        showInResults: field.showInResults,
        truncateColumn: field.truncateColumn,
        searchWeight: field.searchWeight
      };
    });

    const response = await authFetch('/configuration', {
      method: 'POST',
      body: JSON.stringify({
        updates: { [`Analyzers:${config.documentType}:Metadata`]: JSON.stringify(metadata) }
      })
    });
    if (!response.ok) throw new Error(`Failed to save metadata: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to save metadata');

    // Invalidate memoized config so the next getConfiguration() re-fetches from the
    // server — fixes stale-save bug where saved normalize/validation edits were
    // invisible until page refresh (Req 1.1, 6.7).
    configurationService.invalidate();
  }

  /**
   * Preview a field's (possibly unsaved) normalization config against a sample
   * value. Pure computation server-side — no credentials, no tenant data.
   */
  async testNormalization(
    fieldMeta: { normalize?: FieldNormalization; textFormatting?: FieldConfiguration['textFormatting']; format?: string },
    inputValue: string
  ): Promise<string> {
    const response = await authFetch('/configuration/normalize/test', {
      method: 'POST',
      body: JSON.stringify({ fieldMeta, inputValue })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || `Test failed: ${response.status}`);
    return data.transformedValue;
  }

  /**
   * Validate regexReplace patterns against RE2 — the engine that actually runs them
   * at processing time. Native JS RegExp accepts syntax (e.g. lookahead) RE2 rejects,
   * so Save-time validation must check against RE2, not RegExp, or an admin can save
   * a pattern that silently no-ops in production.
   * Returns one error message per rule (null = valid), same order as the input.
   */
  async validateRegexRules(rules: Array<{ pattern: string; flags?: string }>): Promise<Array<string | null>> {
    if (rules.length === 0) return [];
    const response = await authFetch('/configuration/normalize/validate-regex', {
      method: 'POST',
      body: JSON.stringify({ rules })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || `Validation failed: ${response.status}`);
    return data.errors;
  }

  /**
   * Preview SLM normalization against a sample value (Req 4.5).
   * Admin-gated server-side; calls `POST /configuration/normalize/test-slm`.
   * Returns { before, after, tokensUsed }.
   */
  async testSlmNormalization(
    prompt: string,
    sampleValue: string,
    model?: string
  ): Promise<{ before: string; after: string; tokensUsed: number }> {
    const response = await authFetch('/configuration/normalize/test-slm', {
      method: 'POST',
      body: JSON.stringify({ prompt, model: model || undefined, sampleValue })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || `SLM test failed: ${response.status}`);
    return data as { before: string; after: string; tokensUsed: number };
  }

  /** @deprecated Use saveAnalyzerMetadata instead */
  async saveAnalyzerConfiguration(config: AnalyzerConfiguration): Promise<void> {
    return this.saveAnalyzerMetadata(config);
  }

  private async getCustomerConfig(): Promise<any> {
    try {
      // Use the shared configuration service cache — avoids a redundant /configuration
      // API round-trip since the app already loaded config on startup.
      const config = await configurationService.getConfiguration();
      return config || {};
    } catch (error) {
      console.error('Error getting customer configuration:', error);
      return {};
    }
  }

  async getAnalyzerConfigurations(): Promise<AnalyzerConfiguration[]> {
    try {
      const config = await this.getCustomerConfig();
      const analyzers: AnalyzerConfiguration[] = [];

      if (config.analyzers) {
        Object.entries(config.analyzers).forEach(([, metadata]: [string, any]) => {
          if (metadata && metadata.fields) {
            const analyzerConfig: AnalyzerConfiguration = {
              analyzerId: metadata.analyzerId,
              analyzerName: metadata.analyzerId,
              documentType: metadata.documentType,
              enabled: true,
              enableOwnershipInference: false,
              export: metadata.export,
              fields: Object.entries(metadata.fields).map(([fieldName, fieldMeta]: [string, any]) => ({
                fieldName,
                displayName: fieldMeta.displayName,
                // Legacy display type — mirror dataType where compatible (boolean falls back to string)
                fieldType: (['date', 'number', 'currency', 'address'].includes(fieldMeta.dataType)
                  ? fieldMeta.dataType
                  : 'string') as FieldConfiguration['fieldType'],
                enabled: true,
                required: fieldMeta.required,
                readOnly: fieldMeta.readOnly,
                uiGroup: fieldMeta.uiGroup,
                displayOrder: fieldMeta.displayOrder,
                stageUsage: fieldMeta.stageUsage,
                externalMapping: fieldMeta.grmMapping ? { grmField: fieldMeta.grmMapping } : {},
                validation: fieldMeta.validation,
                // Field schema (Sprint M2)
                dataType: fieldMeta.dataType,
                format: fieldMeta.format,
                confidenceThreshold: fieldMeta.confidenceThreshold,
                validationRule: fieldMeta.validationRule,
                addressValidate: fieldMeta.addressValidate,
                // Export field-mapping (Sprint M3)
                exportName: fieldMeta.exportName,
                exportInclude: fieldMeta.exportInclude,
                exportTransform: fieldMeta.exportTransform,
                // Deterministic post-extraction normalization (Sprint N1/N4)
                textFormatting: fieldMeta.textFormatting,
                normalize: fieldMeta.normalize,
                // SLM normalization mode (Processing Pipeline Solidification — Req 4.1)
                normalizeMode: fieldMeta.normalizeMode,
                // Lens field contract (S1), read back so S4's result columns survive a save.
                provenance: fieldMeta.provenance,
                searchable: fieldMeta.searchable,
                // Lens result-column contract (R9), read back so column/order/truncation survive a save.
                showInResults: fieldMeta.showInResults,
                truncateColumn: fieldMeta.truncateColumn,
                searchWeight: fieldMeta.searchWeight
              }))
            };
            analyzers.push(analyzerConfig);
          }
        });
      }

      if (analyzers.length === 0) {
        console.warn('No analyzer metadata found in Configuration Service');
      }
      return analyzers;
    } catch (error) {
      console.error('Error getting analyzer configurations:', error);
      return [];
    }
  }

  async syncAnalyzerToAzure(config: AnalyzerConfiguration): Promise<{ analyzerId: string; status: 'ready' | 'failed' }> {
    const fieldSchema: any = { fields: {} };
    config.fields.forEach(field => {
      fieldSchema.fields[field.fieldName] = {
        type: field.fieldType,
        method: 'extract',
        description: field.extractionDescription || field.displayName || field.fieldName
      };
    });

    // Step 1: Create/update the analyzer in Azure AI
    const response = await authFetch('/analyzers', {
      method: 'POST',
      body: JSON.stringify({
        analyzerId: config.analyzerId,
        description: config.analyzerName,
        baseAnalyzerId: 'prebuilt-document',
        fieldSchema
      })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData.error || errorData.message || `${response.status} ${response.statusText}`;
      throw new Error(`Failed to create analyzer: ${detail}`);
    }
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to create analyzer');

    // Step 2: Poll until the analyzer build completes or fails (max 60 seconds)
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      try {
        const statusResp = await authFetch(`/analyzers/details?analyzerId=${encodeURIComponent(config.analyzerId)}`);
        if (!statusResp.ok) continue;
        const statusData = await statusResp.json();
        const status = statusData.analyzer?.status?.toLowerCase();
        if (status === 'ready' || status === 'succeeded') {
          return { analyzerId: config.analyzerId, status: 'ready' };
        }
        if (status === 'failed') {
          throw new Error(`Analyzer build failed in Azure AI. Check the analyzer configuration and try again.`);
        }
        // Still building — continue polling
      } catch (error) {
        if (error instanceof Error && error.message.includes('Analyzer build failed')) throw error;
        // Transient error — continue polling
      }
    }
    throw new Error('Analyzer build timed out after 60 seconds. Check Azure AI Content Understanding for status.');
  }

  async deleteAnalyzerFile(documentType: string): Promise<void> {
    const response = await authFetch(`/analyzers/file?documentType=${encodeURIComponent(documentType)}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error(`Failed to delete analyzer file: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to delete analyzer file');
  }

  async deleteAnalyzer(analyzerId: string): Promise<void> {
    const response = await authFetch(`/analyzers?analyzerId=${encodeURIComponent(analyzerId)}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error(`Failed to delete analyzer: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to delete analyzer');
  }

  async deleteAnalyzerConfiguration(documentType: string): Promise<void> {
    const metadataKey = `Analyzers:${documentType}:Metadata`;
    const response = await authFetch('/configuration', {
      method: 'POST',
      body: JSON.stringify({ updates: { [metadataKey]: '' } })
    });
    if (!response.ok) throw new Error(`Failed to delete analyzer metadata: ${response.status} ${response.statusText}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'Failed to delete analyzer metadata');

    // Clean up old format key
    try {
      await authFetch('/configuration', {
        method: 'POST',
        body: JSON.stringify({ updates: { [`Analyzers:${documentType}`]: '' } })
      });
    } catch { /* ignore cleanup errors */ }
  }
}

// Singleton instance
const analyzerService = new AnalyzerService();
export default analyzerService;
